import axios from 'axios';

// Fallback URLs when env vars aren't picked up at build time
// Priority: env var > hostname-based detection > local development
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  const hostname = window.location.hostname;

  // Staging environment - ALWAYS use staging backend
  if (hostname.includes('staging-alfred-voice')) {
    console.log('[API] Staging site detected, using staging backend');
    return 'https://callcenter-staging-9528.up.railway.app/api';
  }

  // If env var is set and we're not on staging, use it
  if (envUrl) {
    console.log('[API] Using env var:', envUrl);
    return envUrl;
  }

  // Production Alfred Voice on Netlify
  if (hostname.includes('alfred-voice') && hostname.includes('netlify')) {
    console.log('[API] Production Alfred Voice detected');
    return 'https://callcenter-production.up.railway.app/api';
  }

  // Local development
  console.log('[API] Using local /api');
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const wrapSuccess = (data) => ({ data: { success: true, data } });

const defaultApiSettings = {
  webhookUrl: '',
  apiKeys: {},
  rateLimits: {
    callsPerMinute: 100,
    requestsPerSecond: 10
  }
};

const defaultNotificationSettings = {
  emailNotifications: {
    callAlerts: true,
    systemUpdates: true,
    billingUpdates: true,
    securityAlerts: true
  },
  smsNotifications: {
    criticalAlerts: false,
    callFailures: false
  }
};

const SAMPLE_BILLING = {
  nextBillingDate: new Date(Date.now() + 86400000 * 10).toISOString(),
  plan: 'starter',
  balance: 0,
  paymentMethod: {
    brand: 'Visa',
    last4: '4242',
    expMonth: 12,
    expYear: 2026
  }
};

const SAMPLE_USAGE = {
  summary: {
    minutesUsed: 120,
    minutesIncluded: 1000,
    numbersProvisioned: 1,
    numbersLimit: 5
  },
  categories: [
    { name: 'Inbound', minutes: 80 },
    { name: 'Outbound', minutes: 40 }
  ]
};

const SAMPLE_INVOICES = [
  {
    id: 'INV-001',
    amount: 49,
    status: 'paid',
    issuedAt: new Date(Date.now() - 86400000 * 30).toISOString(),
    dueDate: new Date(Date.now() - 86400000 * 20).toISOString()
  }
];

export const authAPI = {
  sendMagicLink: (data) => api.post('/auth/send-magic-link', data),
  verifyMagicLink: (data) => api.post('/auth/verify-magic-link', data),
  refresh: () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return Promise.reject(new Error('No token available to refresh'));
    }
    return api.post('/auth/refresh', { token });
  },
  logout: () => api.post('/auth/logout', {}),
  verifyToken: () => api.get('/auth/verify'),
  getProfile: () => {
    console.log('API getProfile called');
    return api.get('/user/profile');
  },
  updateProfile: (data) => {
    console.log('API updateProfile called with:', data);
    console.log('Data keys:', Object.keys(data));
    return api.put('/user/profile', data);
  },
  register: (userData) => api.post('/auth/register', userData)
};

export const settingsAPI = {
  getTenant: () => api.get('/settings/tenant'),
  updateTenant: (data) => api.put('/settings/tenant', data),
  getGeneral: () => api.get('/settings/general'),
  updateGeneral: (data) => api.put('/settings/general', data),
  getBusinessHours: () => api.get('/settings/business-hours'),
  updateBusinessHours: (data) => api.put('/settings/business-hours', data),
  getLanguagePreferences: () => api.get('/settings/language-preferences'),
  updateLanguagePreferences: (data) => api.put('/settings/language-preferences', data),
  getUsageLimits: () => api.get('/settings/usage-limits'),
  getAIConfig: () => api.get('/settings/ai-config'),
  updateAIConfig: (data) => api.put('/settings/ai-config', data)
};

export const businessProfileAPI = {
  getProfile: () => api.get('/business/profile'),
  updateProfile: (data) => api.put('/business/profile', data),
  getBusinessHours: () => api.get('/business/hours'),
  updateBusinessHours: (data) => api.put('/business/hours', data),
  getFAQ: () => api.get('/business/faq'),
  updateFAQ: (data) => api.put('/business/faq', data),
  getServices: () => api.get('/business/services'),
  updateServices: (data) => api.put('/business/services', data)
};

export const phoneAPI = {
  getAvailableNumbers: (params) => api.get('/phone/available', { params }),
  provisionNumber: (number) => api.post('/phone/provision', { number }),
  getMyNumbers: () => api.get('/phone/my-numbers'),
  releaseNumber: (number) => api.delete(`/phone/release/${number}`),
  updateNumberSettings: (number, settings) => api.put(`/phone/settings/${number}`, settings)
};

export const conversationFlowAPI = {
  getFlows: () => api.get('/conversation-flows'),
  createFlow: (data) => api.post('/conversation-flows', data),
  updateFlow: (id, data) => api.put(`/conversation-flows/${id}`, data),
  deleteFlow: (id) => api.delete(`/conversation-flows/${id}`),
  testFlow: (id, input) => api.post(`/conversation-flows/${id}/test`, { input }),
  getFlowTemplates: () => api.get('/conversation/templates')
};

export const monitoringAPI = {
  getHealth: () => api.get('/monitoring/health'),
  getMetrics: () => api.get('/monitoring/metrics/realtime'),
  getAlerts: (params = {}) => api.get('/monitoring/alerts', { params }),
  getLogs: (params = {}) => api.get('/monitoring/logs', { params }),
  getDiagnostics: () => api.get('/monitoring/diagnostics'),
  // Live call monitoring
  getActiveCalls: () => api.get('/monitoring/active-calls'),
  endCall: (sessionId, reason = 'supervisor_ended') => api.post(`/monitoring/calls/${sessionId}/end`, { reason })
};

export const billingAPI = {
  getCurrentUsage: () => api.get('/billing/usage/current'),
  getUsageHistory: (months = 6) => api.get(`/billing/usage/history?months=${months}`),
  getInvoices: (limit = 12) => api.get(`/billing/invoices?limit=${limit}`),
  getPlans: () => api.get('/billing/plans'),
  upgradePlan: (data) => api.post('/billing/upgrade', data),
  createCheckoutSession: (data) => api.post('/billing/checkout-session', data),
  getPaymentMethods: () => api.get('/billing/payment-methods'),
  getCostAnalysis: (timeRange = '30d') => api.get(`/billing/cost-analysis?timeRange=${timeRange}`),
  createSetupIntent: () => api.post('/billing/create-setup-intent')
};

/**
 * Orders API - Not yet implemented
 * These endpoints will return 501 Not Implemented from the backend
 */
export const ordersAPI = {
  getOrders: () => api.get('/orders'),
  getOrder: (id) => api.get(`/orders/${id}`),
  updateOrder: (id, data) => api.put(`/orders/${id}`, data),
  createOrder: (data) => api.post('/orders', data)
};

export const usersAPI = {
  getUsers: (params = {}) => api.get('/tenant/users', { params }),
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data) => api.put('/user/profile', data),
  inviteUser: (data) => api.post('/tenant/users/invite', data),
  updateUser: (id, data) => api.put(`/tenant/users/${id}`, data),
  deleteUser: (id) => api.delete(`/tenant/users/${id}`)
};

export const tenantAPI = {
  // Tenant-specific endpoints stay at /tenant/*
  getTenant: () => api.get('/tenant'),
  updateTenant: (data) => api.put('/tenant', data),

  // User management should go through usersAPI
  getUsers: () => usersAPI.getUsers(),  // Delegates to usersAPI
  inviteUser: (data) => usersAPI.inviteUser(data),  // Delegates to usersAPI
  updateUserRole: (id, role) => usersAPI.updateUser(id, { role }),  // Delegates to usersAPI
  removeUser: (id) => usersAPI.deleteUser(id),  // Delegates to usersAPI

  // Tenant-specific usage endpoint
  getUsage: (params = {}) => api.get('/tenant/usage', { params }),

  // Branding endpoints
  getBranding: () => api.get('/tenant/branding'),
  updateBranding: (data) => api.put('/tenant/branding', data),
  getPublicBranding: (params = {}) => api.get('/tenant/branding/public', { params })
};

export const dashboardAPI = {
  // Dashboard Overview
  getDashboardOverview: (timeRange = '24h') =>
    api.get(`/dashboard/overview?timeRange=${timeRange}`),
  getOverview: (timeRange = '24h') =>
    api.get(`/dashboard/overview?timeRange=${timeRange}`),

  // Calls endpoints
  getRecentCalls: (params = {}) => {
    const queryString = new URLSearchParams({
      period: 'today',
      limit: 10,
      ...params
    }).toString();
    return api.get(`/dashboard/calls/recent?${queryString}`);
  },

  getCallDetails: (callId) =>
    api.get(`/calls/${callId}`),

  // Analytics endpoints
  getAnalytics: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/analytics?${queryString}`);
  },

  getTrends: (params = {}) => api.get('/dashboard/analytics/trends', { params }),

  getAnalyticsMetrics: (period = '30d') =>
    api.get(`/analytics/metrics?period=${period}`),

  getAnalyticsChartData: (type, period) =>
    api.get(`/analytics/chart/${type}?period=${period}`),

  // Performance metrics
  getPerformance: () => api.get('/dashboard/performance'),
  getPerformanceMetrics: () =>
    api.get('/dashboard/performance'),

  // Language statistics
  getLanguages: (timeRange = '24h') => api.get(`/dashboard/languages?timeRange=${timeRange}`),
  getLanguageStats: (timeRange = '24h') =>
    api.get(`/dashboard/languages?timeRange=${timeRange}`),

  // Settings endpoints
  getVoiceSettings: () => api.get('/settings/voice'),
  updateVoiceSettings: (settings) => api.put('/settings/voice', settings),
  getGeneralSettings: () => api.get('/settings/general'),
  updateGeneralSettings: (settings) => api.put('/settings/general', settings),
  getApiSettings: () => api.get('/settings/api'),
  updateApiSettings: (data) => api.put('/settings/api', data),
  getNotificationSettings: () => api.get('/settings/notifications'),
  updateNotificationSettings: (data) => api.put('/settings/notifications', data),

  // Profile endpoints (aliased from authAPI for convenience)
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data) => api.put('/user/profile', data),

  // Billing endpoints (aliased from billingAPI for convenience)
  getCurrentUsage: () => api.get('/billing/usage/current'),
  getInvoices: (params) => api.get('/billing/invoices', { params }),
  getCostAnalysis: (params) => api.get('/billing/cost-analysis', { params }),
  getBillingPlans: () => api.get('/billing/plans'),

  // Legacy/compatibility
  updateTenantSettings: (data) => tenantAPI.updateTenant(data)
};

export const callsAPI = {
  getCalls: (params = {}) => api.get('/calls', { params }),
  getCall: (id) => api.get(`/calls/${id}`),
  endCall: (id) => api.post(`/calls/${id}/end`),
  getConversations: (id) => api.get(`/calls/${id}/conversations`),
  scheduleOutboundCall: (payload) => api.post('/calls/schedule', payload, { timeout: 60000 }), // 60s timeout for external API calls
  getScheduledCalls: () => api.get('/calls/scheduled'),
  cleanupStuckCalls: () => api.post('/calls/scheduled/cleanup'), // Clean up stuck in-progress calls
  createOutboundCall: (payload) => api.post('/calls/schedule', payload, { timeout: 60000 }), // Create outbound call (uses schedule endpoint)
  getPhoneNumbers: () => api.get('/phone/my-numbers') // Get available phone numbers
};

export const analyticsAPI = {
  getOverview: (params = {}) => Promise.resolve(wrapSuccess([])),
  getCalls: (params = {}) => Promise.resolve(wrapSuccess([])),
  getPerformance: (params = {}) => Promise.resolve(wrapSuccess([])),
  getLanguages: (params = {}) => Promise.resolve(wrapSuccess([])),
  getConversations: (params = {}) => Promise.resolve(wrapSuccess([]))
};

export const knowledgeAPI = {
  // Articles
  getArticles: (params = {}) => api.get('/knowledge/articles', { params }),
  createArticle: (data) => api.post('/knowledge/articles', data),
  updateArticle: (id, data) => api.put(`/knowledge/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/knowledge/articles/${id}`),

  // Documents
  getDocuments: (params = {}) => api.get('/knowledge/documents', { params }),
  createDocument: (data) => api.post('/knowledge/documents', data),
  updateDocument: (id, data) => api.put(`/knowledge/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/knowledge/documents/${id}`),

  // Categories
  getCategories: () => api.get('/knowledge/categories')
};

export const recordingsAPI = {
  // Get audio blob for playback (with authentication)
  getAudio: async (sessionId, format = 'wav') => {
    const response = await api.get(`/recordings/${sessionId}/audio`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  },

  // Get recording metadata
  getMetadata: (sessionId) => api.get(`/recordings/${sessionId}/metadata`),

  // List all recordings
  list: () => api.get('/recordings'),

  // Delete recording
  delete: (sessionId) => api.delete(`/recordings/${sessionId}`)
};

export const salesAPI = {
  // Campaign Management
  getCampaigns: (params = {}) => api.get('/sales-campaigns', { params }),
  getCampaign: (id) => api.get(`/sales-campaigns/${id}`),
  createCampaign: (data) => api.post('/sales-campaigns', data),
  updateCampaign: (id, data) => api.patch(`/sales-campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/sales-campaigns/${id}`),
  startCampaign: (id) => api.post(`/sales-campaigns/${id}/start`),
  pauseCampaign: (id) => api.post(`/sales-campaigns/${id}/pause`),
  getCampaignStats: (id) => api.get(`/sales-campaigns/${id}/stats`),

  // Lead Management
  getLeads: (campaignId, params = {}) => api.get(`/sales/campaigns/${campaignId}/leads`, { params }),
  getLead: (id) => api.get(`/sales/leads/${id}`),
  createLead: (campaignId, data) => api.post(`/sales/campaigns/${campaignId}/leads`, data),
  updateLead: (id, data) => api.patch(`/sales/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/sales/leads/${id}`),
  importLeads: (campaignId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/sales/campaigns/${campaignId}/leads/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateDisposition: (id, data) => api.patch(`/sales/leads/${id}/disposition`, data),
  scheduleCall: (id, data) => api.post(`/sales/leads/${id}/schedule-call`, data),

  // Call Attempts
  getCallAttempts: (leadId) => api.get(`/sales/leads/${leadId}/attempts`),

  // External Leads Import
  getExternalSources: () => api.get('/sales/leads/external-sources'),
  previewExternalLeads: (data) => api.post('/sales/leads/preview-external', data),
  importExternalLeads: (data) => api.post('/sales/leads/import-external', data)
};

export const leadCategoriesAPI = {
  // Lead Categories CRUD
  getCategories: (params = {}) => api.get('/lead-categories', { params }),
  getCategory: (id) => api.get(`/lead-categories/${id}`),
  createCategory: (data) => api.post('/lead-categories', data),
  updateCategory: (id, data) => api.patch(`/lead-categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/lead-categories/${id}`),
  assignLeads: (id, leadIds) => api.post(`/lead-categories/${id}/assign`, { leadIds }),
  getTemplates: () => api.get('/lead-categories/defaults/templates')
};

export const demoAreaAPI = {
  // Demo Area CRUD
  getDemoAreas: () => api.get('/demo/areas'),
  getDemoArea: (id) => api.get(`/demo/areas/${id}`),
  createDemoArea: (data) => api.post('/demo/areas', data),
  updateDemoArea: (id, data) => api.put(`/demo/areas/${id}`, data),
  deleteDemoArea: (id) => api.delete(`/demo/areas/${id}`),
  getDemoAreaByPhone: (phoneNumber) => api.get(`/demo/areas/by-phone/${encodeURIComponent(phoneNumber)}`)
};

export const ivrAudioAssetsAPI = {
  // IVR Audio Assets CRUD
  getAssets: () => api.get('/ivr/audio-assets'),
  getAsset: (id) => api.get(`/ivr/audio-assets/${id}`),
  createAsset: (data) => api.post('/ivr/audio-assets', data),
  updateAsset: (id, data) => api.put(`/ivr/audio-assets/${id}`, data),
  deleteAsset: (id) => api.delete(`/ivr/audio-assets/${id}`),
  generateAudio: (id) => api.post(`/ivr/audio-assets/${id}/generate`),
  previewAudio: (data) => api.post('/ivr/audio-assets/preview', data),
  getVoices: (provider) => api.get(`/ivr/audio-assets/voices/${provider}`)
};

export const employeeCommunicationsAPI = {
  // Employee Management
  getEmployees: (params = {}) => api.get('/employee-communications/employees', { params }),
  getEmployee: (id) => api.get(`/employee-communications/employees/${id}`),
  createEmployee: (data) => api.post('/employee-communications/employees', data),
  updateEmployee: (id, data) => api.put(`/employee-communications/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/employee-communications/employees/${id}`),
  importEmployees: (data) => api.post('/employee-communications/employees/import', data),

  // Staff Directory Integration
  importFromStaff: (data) => api.post('/employee-communications/employees/import-from-staff', data),
  syncFromStaff: (employeeId) => api.post(`/employee-communications/employees/${employeeId}/sync-from-staff`),
  linkToStaff: (employeeId, staffMemberId) => api.post(`/employee-communications/employees/${employeeId}/link-staff`, { staffMemberId }),
  getAvailableStaff: () => api.get('/employee-communications/staff-available'),

  // Campaign Management
  getCampaigns: (params = {}) => api.get('/employee-communications/campaigns', { params }),
  getCampaign: (id) => api.get(`/employee-communications/campaigns/${id}`),
  createCampaign: (data) => api.post('/employee-communications/campaigns', data),
  updateCampaign: (id, data) => api.put(`/employee-communications/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/employee-communications/campaigns/${id}`),
  startCampaign: (id) => api.post(`/employee-communications/campaigns/${id}/start`),
  pauseCampaign: (id) => api.post(`/employee-communications/campaigns/${id}/pause`),

  // Communication History
  getHistory: (params = {}) => api.get('/employee-communications/history', { params }),
  getCallDetails: (id) => api.get(`/employee-communications/calls/${id}`)
};

// Integrations API (GHL, HubSpot, Zapier, etc.)
export const integrationsAPI = {
  // List all integrations
  getAll: () => api.get('/integrations'),

  // Get specific integration config
  getConfig: (type) => api.get(`/integrations/${type}/config`),

  // GHL Integration
  setupGHL: (data) => api.post('/integrations/ghl/setup', data),

  // HubSpot Integration
  setupHubSpot: (data) => api.post('/integrations/hubspot/setup', data),
  startHubSpotOAuth: () => api.get('/integrations/hubspot/oauth/start'),
  createHubSpotProperties: () => api.post('/integrations/hubspot/create-properties'),

  // Test connection
  test: (type, data) => api.post(`/integrations/${type}/test`, data),

  // Disconnect integration
  disconnect: (type) => api.delete(`/integrations/${type}`)
};

export const dialerAPI = {
  initiateCall: (data) => api.post('/dialer/call', data),
  hangup: (data) => api.post('/dialer/hangup', data),
  getHistory: () => api.get('/dialer/history'),
  transcribe: (callId) => api.post(`/dialer/transcribe/${callId}`)
};

export const screeningAPI = {
  getSettings: () => api.get('/screening/settings'),
  updateSettings: (data) => api.put('/screening/settings', data),

  getCategories: () => api.get('/screening/categories'),
  createCategory: (data) => api.post('/screening/categories', data),
  updateCategory: (id, data) => api.patch(`/screening/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/screening/categories/${id}`),

  getRules: () => api.get('/screening/rules'),
  createRule: (data) => api.post('/screening/rules', data),
  updateRule: (id, data) => api.patch(`/screening/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/screening/rules/${id}`),

  getVoicemails: (params = {}) => api.get('/screening/voicemails', { params }),
  updateVoicemail: (id, data) => api.patch(`/screening/voicemails/${id}`, data),
  deleteVoicemail: (id) => api.delete(`/screening/voicemails/${id}`),

  importContacts: (formData) =>
    api.post('/screening/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
};

export default api;
